import axios from "axios";
import { DEFAULT_COACH_RATES, toSpeechText } from "../CoachVoice";
import { buildReplayReviewComments, splitNameMarkup } from "../../services/replayReviewComments";

// Narrador del video de la repetición (videonarrador1): los comentarios de «★ Análisis del bot»
// como voz (pedida al servidor, /api/narrator/tts) y/o subtítulos. El video se arma en el
// navegador (public/replay-video/exporter.js, options.narration).

export const NARRATION_MODES = [
  { id: "none", icon: "▶", label: "Video normal", hint: "Sin narrador." },
  { id: "voice", icon: "🔊", label: "Con voz del narrador", hint: "El bot comenta cada jugada en voz alta." },
  { id: "voice-subs", icon: "💬", label: "Con voz y subtítulos", hint: "Voz y texto abajo: se entiende sin sonido." },
];
const MODE_KEY = "xima_video_narration_v1";
// Los mismos que la bocina (CoachVoice.js).
const GENDER_KEY = "xima_coach_voice_gender";
// Velocidad por voz (vozentrenador4); la clave única de antes, si aún no se migró.
const RATE_KEYS = { male: "xima_coach_voice_rate_male", female: "xima_coach_voice_rate_female" };
const LEGACY_RATE_KEY = "xima_coach_voice_rate";
const PARALLEL = 3;

export const loadNarrationMode = () => {
  try {
    const saved = window.localStorage.getItem(MODE_KEY);
    return NARRATION_MODES.some((mode) => mode.id === saved) ? saved : "none";
  } catch (_error) {
    return "none";
  }
};

export const saveNarrationMode = (mode) => {
  try {
    window.localStorage.setItem(MODE_KEY, mode);
  } catch (_error) {
    // Sin almacenamiento: vale solo esta vez.
  }
};

// Sitio de los subtítulos (videonarrador2): { landscape: {x, y} | null, portrait: {x, y} | null },
// centro de la caja en fracción del ancho/alto del video. null = abajo centrado (lo de siempre).
const SUBTITLE_POS_KEY = "xima_video_subtitle_pos_v1";
export const SUBTITLE_SAMPLE_TEXT = "Aquí se verán los subtítulos al descargar el video";

const normalizeSubtitlePoint = (point) => {
  const x = Number(point?.x);
  const y = Number(point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
};

export const loadSubtitlePositions = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SUBTITLE_POS_KEY) || "{}");
    return { landscape: normalizeSubtitlePoint(saved?.landscape), portrait: normalizeSubtitlePoint(saved?.portrait) };
  } catch (_error) {
    return { landscape: null, portrait: null };
  }
};

export const saveSubtitlePositions = (value) => {
  try {
    window.localStorage.setItem(SUBTITLE_POS_KEY, JSON.stringify(value));
  } catch (_error) {
    // Sin almacenamiento: vale solo esta visita.
  }
};

// Mismas medidas que drawSubtitlePage de public/replay-video/exporter.js, para un video de width × height.
export const subtitleMetrics = (width, height) => {
  const portrait = height > width;
  const fontSize = portrait ? width * 0.046 : height * 0.04;
  return {
    fontSize,
    maxTextWidth: width * (portrait ? 0.88 : 0.8),
    bottomGap: height * (portrait ? 0.05 : 0.045),
  };
};

export const readVoicePrefs = () => {
  let gender = "male";
  let rate = DEFAULT_COACH_RATES?.male || 1;
  try {
    gender = window.localStorage.getItem(GENDER_KEY) === "female" ? "female" : "male";
    rate = DEFAULT_COACH_RATES?.[gender] || 1;
    const raw = window.localStorage.getItem(RATE_KEYS[gender]) ?? window.localStorage.getItem(LEGACY_RATE_KEY);
    const stored = Number(raw);
    if (raw !== null && Number.isFinite(stored) && stored >= 0.5 && stored <= 2) rate = stored;
  } catch (_error) {
    // valores por defecto
  }
  return { gender, rate };
};

// ¿Este navegador puede meter sonido en el MP4?
export const canEncodeAudio = () =>
  typeof window !== "undefined" &&
  typeof window.AudioEncoder === "function" &&
  Boolean(window.OfflineAudioContext || window.webkitOfflineAudioContext);

// Lado del que mira (igual que replayReviewViewer de la repetición si no viene dado).
const fallbackViewer = (viewer, meta) => {
  if (viewer && Number(viewer.playerIndex) >= 0) return viewer;
  const players = Array.isArray(meta?.players) ? meta.players : [];
  const bots = players.filter((player) => player?.isBot);
  const playerIndex =
    players.length === 2 && bots.length === 1 ? players.findIndex((player) => !player?.isBot) : -1;
  return { name: viewer?.name || "", playerIndex };
};

// { [step]: texto } con los mismos comentarios del panel del análisis.
export const buildNarrationTexts = ({ moves, meta, comments, seed, viewer }) => {
  let source = comments && Object.keys(comments).length ? comments : null;
  if (!source && Array.isArray(moves) && moves.length) {
    source = buildReplayReviewComments(moves, meta, seed || "", fallbackViewer(viewer, meta));
  }
  const texts = {};
  Object.keys(source || {}).forEach((step) => {
    const plain = splitNameMarkup(source[step] || "")
      .map((part) => part.text)
      .join("")
      .replace(/\s+/g, " ")
      .trim();
    if (plain && Number(step) > 0) texts[Number(step)] = plain;
  });
  return texts;
};

let decodeContext = null;
const decodeAudio = async (arrayBuffer) => {
  const Context = window.AudioContext || window.webkitAudioContext;
  if (!decodeContext) decodeContext = new Context();
  return new Promise((resolve, reject) => {
    const result = decodeContext.decodeAudioData(arrayBuffer, resolve, reject);
    if (result && typeof result.then === "function") result.then(resolve, reject);
  });
};

// Pide la voz de cada comentario. Devuelve { steps: { [step]: { text, buffer } }, failed }.
export const fetchNarrationVoices = async (texts, { gender, rate, signal, onProgress }) => {
  const steps = Object.keys(texts).map(Number).sort((a, b) => a - b);
  const result = {};
  let done = 0;
  let failed = 0;
  let firstError = null;
  let next = 0;
  const worker = async () => {
    while (next < steps.length) {
      const step = steps[next];
      next += 1;
      if (signal?.aborted) return;
      const text = texts[step];
      let buffer = null;
      try {
        const response = await axios.post(
          "/narrator/tts",
          { text: toSpeechText(text), gender, rate },
          { responseType: "arraybuffer", signal, timeout: 45000 }
        );
        buffer = await decodeAudio(response.data);
      } catch (error) {
        if (signal?.aborted) return;
        failed += 1;
        if (!firstError) firstError = error;
        // Si falla todo (sin voz en el servidor) no se sigue insistiendo.
        if (error?.response?.status === 429 || (failed >= 3 && done === 0)) next = steps.length;
      }
      result[step] = { text, buffer };
      done += 1;
      if (typeof onProgress === "function") onProgress(done, steps.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, steps.length) }, worker));
  if (signal?.aborted) {
    const abort = new Error("Cancelado.");
    abort.name = "AbortError";
    throw abort;
  }
  steps.forEach((step) => {
    if (!result[step]) result[step] = { text: texts[step], buffer: null };
  });
  const voiced = steps.filter((step) => result[step].buffer).length;
  return { steps: result, total: steps.length, voiced, failed: steps.length - voiced, error: firstError };
};

// Solo texto (subtítulos sin voz).
export const textOnlyNarration = (texts) => {
  const steps = {};
  Object.keys(texts).forEach((step) => {
    steps[step] = { text: texts[step], buffer: null };
  });
  return steps;
};
